// TMOS Setup Wizard - KPI Setup Step
// Date: December 19, 2025
// Purpose: KPI setup step for tenant admin setup

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';

interface KPISetupStepProps {
  setupData: Record<string, any>;
  onComplete: (data: Record<string, any>) => void;
  onNext: () => void;
  loading: boolean;
}

export const KPISetupStep: React.FC<KPISetupStepProps> = ({
  setupData,
  onComplete,
  loading
}) => {
  const { t } = useLanguage();
  const [useBaseKPIs, setUseBaseKPIs] = useState(setupData.useBaseKPIs !== false);
  const [importIndustryKPIs, setImportIndustryKPIs] = useState(setupData.importIndustryKPIs || false);
  const [industry, setIndustry] = useState(setupData.industry || '');

  const handleContinue = () => {
    onComplete({
      kpi_setup: {
        useBaseKPIs,
        importIndustryKPIs,
        industry: importIndustryKPIs ? industry : undefined
      },
      useBaseKPIs,
      importIndustryKPIs,
      industry: importIndustryKPIs ? industry : undefined
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 mb-6">
        {t('setupWizard.kpi.description') || 
          'Base KPIs are automatically included. You can also import industry-specific KPIs or create custom ones later.'}
      </p>

      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
          <Checkbox
            checked={useBaseKPIs}
            onChange={(checked) => setUseBaseKPIs(checked)}
            disabled
          />
          <div className="flex-1">
            <div className="font-medium text-gray-900">
              {t('setupWizard.kpi.baseKPIs') || 'Base KPIs'}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {t('setupWizard.kpi.baseKPIsDescription') || 
                'Essential KPIs for transformation management (automatically included)'}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
          <Checkbox
            checked={importIndustryKPIs}
            onChange={(checked) => setImportIndustryKPIs(checked)}
          />
          <div className="flex-1">
            <div className="font-medium text-gray-900">
              {t('setupWizard.kpi.industryKPIs') || 'Industry-Specific KPIs'}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {t('setupWizard.kpi.industryKPIsDescription') || 
                'Import KPIs tailored to your industry'}
            </div>
            {importIndustryKPIs && (
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select industry...</option>
                <option value="technology">Technology</option>
                <option value="finance">Finance</option>
                <option value="healthcare">Healthcare</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="retail">Retail</option>
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={loading}>
          {t('common.continue') || 'Continue'}
        </Button>
      </div>
    </div>
  );
};

